/**
 * BikeComparisonView — tabbed view for the bike comparison result.
 *
 * Tabs: Race Dynamics | Map & Ghost Race | Climbs | Descents | Summary
 */
import { useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../theme';
import BikeGapChart from './bike/comparison/BikeGapChart';
import BikeComparisonMetricCharts from './bike/comparison/BikeComparisonMetricCharts';
import BikeGhostRaceMap from './bike/comparison/BikeGhostRaceMap';
import BikeGhostScrubber from './bike/comparison/BikeGhostScrubber';
import BikeClimbProfileChart from './bike/comparison/BikeClimbProfileChart';
import BikeDescentProfileChart from './bike/comparison/BikeDescentProfileChart';
import BikeComparisonClimbsGrid from './bike/comparison/BikeComparisonClimbsGrid';
import BikeComparisonDescentsGrid from './bike/comparison/BikeComparisonDescentsGrid';
import BikeComparisonSummary from './bike/comparison/BikeComparisonSummary';

export default function BikeComparisonView({ result, analysisA, analysisB, resampled }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [scrubberTime, setScrubberTime] = useState(0);
  const [selectedClimb, setSelectedClimb] = useState(null);
  const [selectedDescent, setSelectedDescent] = useState(null);

  if (!result) return null;

  const alignment = result.alignment;
  const rows = resampled || alignment?.resampled || [];
  const commonClimbs = alignment?.common_climbs || [];
  const commonDescents = alignment?.common_descents || [];

  // Prefer props-level analysisA/B (swap-aware) over result fields
  const a = analysisA || result.analysis_a;
  const b = analysisB || result.analysis_b;

  return (
    <Paper sx={{ mt: 2, backgroundColor: colors.grey[800], borderRadius: '8px', overflow: 'hidden' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        scrollButtons={isMobile ? 'auto' : false}
        sx={{
          backgroundColor: colors.grey[900],
          borderBottom: `1px solid ${colors.grey[700]}`,
          '& .MuiTab-root': { color: colors.grey[300] },
          '& .Mui-selected': { color: colors.blueAccent[300] },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
        }}
      >
        <Tab label="Race Dynamics" />
        <Tab label="Map & Ghost Race" />
        <Tab label="Climbs" />
        <Tab label="Descents" />
        <Tab label="Summary" />
      </Tabs>

      <Box p={isMobile ? 1 : 2}>

        {/* Tab 0: Race Dynamics */}
        {tab === 0 && (
          <Box>
            <Typography variant="h6" color={colors.grey[300]} mb={2}>Time Gap</Typography>
            <BikeGapChart resampled={rows} />
            <Box mt={4}>
              <Typography variant="h6" color={colors.grey[300]} mb={2}>Metrics Comparison</Typography>
              <BikeComparisonMetricCharts resampled={rows} />
            </Box>
          </Box>
        )}

        {/* Tab 1: Map & Ghost Race */}
        {tab === 1 && (
          <Box>
            <BikeGhostRaceMap
              analysisA={a}
              analysisB={b}
              resampled={rows}
              scrubberTime={scrubberTime}
            />
            <Box mt={2}>
              <BikeGhostScrubber
                resampled={rows}
                scrubberTime={scrubberTime}
                onTimeChange={setScrubberTime}
              />
            </Box>
          </Box>
        )}

        {/* Tab 2: Climbs */}
        {tab === 2 && (
          <Box>
            <BikeClimbProfileChart
              resampled={rows}
              commonClimbs={commonClimbs}
              climbsA={a?.climbs || []}
              selectedClimb={selectedClimb}
              onClimbClick={setSelectedClimb}
            />
            <Box mt={3}>
              <Typography variant="h6" color={colors.grey[300]} mb={2}>
                Common Climbs ({commonClimbs.length})
              </Typography>
              <BikeComparisonClimbsGrid commonClimbs={commonClimbs} />
            </Box>
          </Box>
        )}

        {/* Tab 3: Descents */}
        {tab === 3 && (
          <Box>
            <BikeDescentProfileChart
              resampled={rows}
              commonDescents={commonDescents}
              descentsA={a?.descents || []}
              selectedDescent={selectedDescent}
              onDescentClick={setSelectedDescent}
            />
            <Box mt={3}>
              <Typography variant="h6" color={colors.grey[300]} mb={2}>
                Common Descents ({commonDescents.length})
              </Typography>
              <BikeComparisonDescentsGrid commonDescents={commonDescents} />
            </Box>
          </Box>
        )}

        {/* Tab 4: Summary */}
        {tab === 4 && (
          <BikeComparisonSummary analysisA={a} analysisB={b} />
        )}

      </Box>
    </Paper>
  );
}
